<template>
  <div class="video-navigation-editor" :class="disabled && 'disabled'">
    <draggable
      v-model="videoList"
      class="video-container"
      tag="div"
      handle=".drag-btn"
      animation="200"
      ghostClass="ghost"
    >
      <div
        v-for="(video, index) in videoList"
        class="video-item"
        :key="index"
        :class="[(video.errorFile || video.errorDesc || video.errorDesc) && 'is-error', !video.url && 'is-empty']"
      >
        <div v-show="video.error" class="error-message">{{ video.error }}</div>
        <div class="video">
          <div class="uploader-mask">
            <div v-show="!video.url" class="add-btn-container" @click="openResourceLibrary(video, ACTION_TYPE.ADD)">
              <div class="btn-icon"></div>
              <el-button class="btn-label" type="text">{{ $t(`${i18nPrefix}.btn.customContent.addVideo`) }}</el-button>
            </div>
            <div v-show="video.url" class="edit-btn-group">
              <div class="edit-btn drag-btn" :title="$t(`common.btn.move`)">
                <i class="iconfont iconMore"></i>
              </div>
              <div class="edit-btn replace-btn" :title="$t(`common.btn.replace`)" @click="openResourceLibrary(video, ACTION_TYPE.REPLACE)">
                <i class="el-icon-edit-outline"></i>
              </div>
              <div class="edit-btn delete-btn" :title="$t(`common.btn.delete`)" @click="handleDeleteBtnClick(video, ACTION_TYPE.DELETE)">
                <i class="el-icon-circle-close"></i>
              </div>
            </div>
          </div>
          <video v-if="video.url" class="video-player" controls controlslist="nodownload" :src="video.url" ></video>
          <div v-show="video.errorFile" class="error-message">{{ video.errorFile }}</div>
        </div>
        <div class="avatar-container">
          <div v-if="!video.avatar" class="default-img el-icon-plus" @click="handlePhotoSelectBtnClick(video)"></div>
          <img v-else class="avatar" :src="getPhotoPath(video.avatar)"/>
          <div class="add-btn" @click="handlePhotoSelectBtnClick(video)">{{ $t(`${i18nPrefix}.btn.customContent.addAvatar`) }}</div>
          <input
            class="file-selector"
            type="file"
            :ref="`fileSelector_${video.vid}`"
            :accept="acceptPhotoFileTypeList.join(',')"
            @change="handlePhotoSelectorChange(video)"
          />
        </div>
        <div v-show="video.errorAvatar" class="error-message">{{ video.errorAvatar }}</div>
        <el-input
          v-model="video.description"
          class="video-desc"
          :placeholder="$t(`${i18nPrefix}.placeholder.customContent.videoDescription`)"
          @input="onDescriptionChange(video)"
        />
        <div v-show="video.errorDesc" class="error-message">{{ video.errorDesc }}</div>
      </div>
    </draggable>
    <ResourceLibraryDialog
      :show.sync="resourceLibraryDialogShow"
      :isReplace="this.currentAction === ACTION_TYPE.REPLACE"
      :editVideo="editContent"
      :appendToBody="appendToBody"
      @confirm="handleSelectResource"
    ></ResourceLibraryDialog>
  </div>
</template>

<script>
import _ from 'lodash'
import draggable from 'vuedraggable'
import { FILE_TYPE } from './uploader.vue'
import { mapGetters } from 'vuex'
import { uploadFile, getFilePath } from '@/utils/qiniu'
import ResourceLibraryDialog from './resourceLibraryDialog.vue'

export const ACTION_TYPE = {
  ADD: 'add',
  REPLACE: 'replace',
  DELETE: 'delete'
}

export default {
  components: {
    draggable,
    ResourceLibraryDialog
  },
  props: {
    rootCompanyId: {
      required: true
    },
    languageCode: {
      type: String
    },
    appendToBody: {
      type: Boolean,
      default: false
    },
    data: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      FILE_TYPE,
      ACTION_TYPE,
      i18nPrefix: 'admin.job.editor',
      vid: 0,
      editContent: null,
      resourceLibraryDialogShow: false,
      acceptPhotoFileTypeList: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'],
      currentAction: null,
      videoList: []
    }
  },
  computed: {

  },
  mounted () {

  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    async submit () {
      // validate
      let hasError = false
      const videoList = _.filter(this.videoList, item => item.url)
      _.forEach(videoList, item => {
        item.errorDesc = null
        item.errorAvatar = null
        item.errorFile = null

        if (!item.description) {
          item.errorDesc = this.$t(`${this.i18nPrefix}.message.requireDescription`)
          hasError = true
        }
        if (!item.avatar) {
          item.errorAvatar = this.$t(`${this.i18nPrefix}.message.requireAvatar`)
          hasError = true
        }
        if (!item.url) {
          item.errorFile = this.$t(`${this.i18nPrefix}.message.requireCustomVideo`)
          hasError = true
        }
      })

      if (hasError) {
        this.$nextTick(() => {
          this.$el.querySelector('.video-item.is-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
        return null
      }

      const avatarUploadPromise = []
      _.forEach(videoList, item => {
        if (item.isUploadedAvatar) {
          return
        }
        const fileKey = this.fileKeyGenerator(item)
        if (!item.avatarFile) {
          return
        }
        avatarUploadPromise.push(new Promise(resolve => {
          uploadFile(item.avatarFile, fileKey, true).then((resp) => {
            item.avatar = fileKey
            item.isUploadedAvatar = true
            resolve(fileKey)
          })
        }))
      })
      await Promise.all(avatarUploadPromise)

      return {
        page_type: 'mobile',
        language_code: this.languageCode,
        video_list: _.map(videoList, (item, index) => {
          return {
            vid: item.vid,
            video_id: item.video_id,
            order: index,
            url: item.url,
            description: item.description,
            avatar: getFilePath(item.avatar),
            hasError: false,
            isUploadedAvatar: item.isUploadedAvatar
          }
        })
      }
    },
    onDescriptionChange (video) {
      if (video.description) {
        video.errorDesc = null
      }
    },
    fileKeyGenerator (video) {
      return `video_navigation/avatar/${this.rootCompanyId}/${new Date().valueOf()}${video.vid || ''}`
    },
    handleAddVideoBtnClick () {
      const videoList = this.videoList
      if (videoList?.length >= 7) {
        return
      }
      videoList.push({
        vid: ++this.vid,
        description: null,
        url: null,
        avatar: null,
        avatarFile: null,
        error: null,
        errorFile: null,
        errorAvatar: null,
        errorDesc: null
      })
    },
    handleDeleteBtnClick (video) {
      if (this.disabled) {
        return
      }
      const index = _.findIndex(this.videoList, item => item.vid === video.vid)
      this.videoList.splice(index, 1)
      if (this.videoList.length === 6) {
        this.handleAddVideoBtnClick()
      }
    },
    openResourceLibrary (video, action) {
      if (this.disabled) {
        return
      }
      this.currentAction = action
      this.editContent = video
      this.resourceLibraryDialogShow = true
    },
    replaceVideo (checkedResourceList) {
      const checkedResource = checkedResourceList[0]
      const video = _.find(this.videoList, { vid: this.editContent?.vid })
      video.url = checkedResource.resource_url
      video.video_id = checkedResource.id
    },
    addVideo (checkedResourceList) {
      _.forEach(checkedResourceList, checkedResource => {
        const video = _.last(this.videoList)
        video.url = checkedResource.resource_url
        video.video_id = checkedResource.id
        this.handleAddVideoBtnClick()
      })
    },
    handleSelectResource (checkedResourceList) {
      if (this.currentAction === ACTION_TYPE.REPLACE) {
        this.replaceVideo(checkedResourceList)
      } else if (this.currentAction === ACTION_TYPE.ADD) {
        this.addVideo(checkedResourceList)
      }

      this.resourceLibraryDialogShow = false
    },
    getPhotoPath (photo) {
      return getFilePath(photo)
    },
    handlePhotoSelectBtnClick (video) {
      if (this.disabled) {
        return
      }
      this.$refs[`fileSelector_${video.vid}`][0].click()
    },
    handlePhotoSelectorChange (video) {
      const file = this.$refs[`fileSelector_${video.vid}`][0].files[0]
      if (!file) {
        return
      }
      if (!this.acceptPhotoFileTypeList.includes(file.type)) {
        this.$message.error({
          type: 'error',
          showClose: true,
          message: this.$t(`${this.i18nPrefix}.message.wrongPhotoFileType`)
        })
        return true
      }
      video.avatarFile = file
      video.avatar = window.URL.createObjectURL(file)
      video.isUploadedAvatar = false
      video.errorAvatar = null
    }
  },
  watch: {
    data: {
      handler () {
        this.videoList = _.chain(this.data)
          .orderBy(['order'])
          .map(item => {
            if (item.vid) {
              return item
            }
            const isUploaded = _.has(item, 'isUploadedAvatar')
            if (!isUploaded) {
              item.isUploadedAvatar = true
            }
            item.vid = ++this.vid
            return {
              ...item,
              errorFile: null,
              errorAvatar: null,
              errorDesc: null
            }
          }).value()

        if (_.every(this.videoList, item => item.url)) {
          this.handleAddVideoBtnClick()
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.video-navigation-editor {
  padding: 15px;
  border: 1px solid #DCDFE6;
  border-radius: 5px;

  &.disabled {
    .video-container {
      .video-item {
        .avatar-container {
          .default-img {
            cursor: not-allowed;

            &:hover {
              color: #ccc;
              border-color: #ccc;
            }
          }

          .add-btn {
            cursor: not-allowed;

            &:hover {
              color: #ccc;
              }
          }
        }
      }

      .video {
        .uploader-mask {
          .edit-btn-group {
            .edit-btn {
              cursor: not-allowed;
            }
          }
        }
      }
    }
  }

  .video-container {
    display: flex;
    flex-wrap: wrap;

    .video-item {
      position: relative;
      margin-right: 20px;
      margin-bottom: 20px;

      .error-message {
        margin-top: 2px;
        margin-bottom: 2px;
        font-size: 12px;
        color: #F56C6C;
        line-height: 12px;
      }

      .video {
        position: relative;
        height: 150px;
        width: 240px;
        background-color: #fff;
        border: 1px dashed #ccc;

        &:hover {
          .edit-btn-group {
            transform: translateY(0);
          }
        }

        .video-player {
          height: 100%;
          width: 100%;
          background-color: #000;
        }
      }

      .avatar-container {
        display: flex;
        align-items: center;
        margin-top: 15px;

        .avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
        }

        .default-img {
          width: 40px;
          height: 40px;
          line-height: 40px;
          border-radius: 50%;
          text-align: center;
          border: 1px dashed #ccc;
          font-size: 16px;
          color: #ccc;
          cursor: pointer;

          &:hover {
            color: #0096cc;
            border-color: #0096cc;
          }
        }

        .add-btn {
          margin-left: 10px;
          font-size: 14px;
          color: #ccc;
          cursor: pointer;

          &:hover {
            color: #0096cc;
          }
        }

        .file-selector {
          display: none;
        }
      }

      .video-desc {
        margin-top: 12px;

        ::v-deep .el-input__inner {

        }
      }

      &.is-empty {
        .uploader-mask {
          height: 100%;

          .add-btn {
            display: flex;
          }
        }
      }

      .uploader-mask {
        display: flex;
        width: 100%;
        height: 100%;
        justify-content: center;
        align-items: center;
        overflow: hidden;
        position: absolute;
        top: 0;
        left: 0;
        border-radius: 4px;

        .add-btn-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin: 0 auto;
          line-height: 1;
          cursor: pointer;

          .btn-icon {
            width: 60px;
            height: 60px;
            background-repeat: no-repeat;
            background-size: cover;
            background-image: url($baseDomain + 'static/image/icon_add_video.png');
          }

          .btn-label {
            padding-bottom: 0;
          }
        }

        .edit-btn-group {
          display: flex;
          align-items: center;
          width: 100%;
          background: rgba($color: #000000, $alpha: 0.3);
          transition: transform .3s;
          transform: translateY(-100%);
          justify-content: flex-end;
          position: absolute;
          top: 0;
          z-index: 1;

          .edit-btn {
            font-size: 22px;
            margin-right: 10px;
            cursor: pointer;

            &.replace-btn {
              color: #0096cc;
            }

            &.drag-btn {
              .iconMore {
                font-size: 22px;
              }
            }

            &.delete-btn {
              color: #fb4646;
            }
          }

        }
      }
    }
  }
}
</style>
